// avisos.js — los posts recientes de Actualidad, para la puerta de la portada.
//
// Endpoint: https://listen.astra.fm/avisos.json?fuente=actualidad
//
// El servidor publica **los últimos 30 días ya ordenados y filtrados**: solo
// Actualidad, ni un vídeo ni nada más. La ventana la decide él y la dice en
// `ventanaDias`, así que la web no filtra por fecha ni por categoría.
//
// 🔴 Esto sustituye a `fetchRadar(4, "lanzamientos", 7)`, que hacía tres cosas
// que no le tocaban: descargar los 427 KB de `actualidad.json` enteros para
// pintar cuatro tarjetas, decidir aquí la ventana temporal y decidir aquí qué
// categoría es «lanzamientos». Con siete días la puerta llegó a quedarse con un
// solo disco —comprobado el 31.08.26— y podía desaparecer de la portada si no se
// publicaba nada en una semana.
//
// ⚠️ A diferencia de `actualidad.json`, este endpoint SÍ atiende a la query: sin
// `fuente=actualidad` devuelve además otras cosas. El parámetro no es adorno.

import { LISTEN } from "./host";

const URL = `${LISTEN}/avisos.json?fuente=actualidad`;

// Un minuto, el mismo `max-age` que manda el servidor: cachear aquí más tiempo
// que allí sería contradecirle.
const TTL = 60 * 1000;

let cache = null;
let cacheAt = 0;

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** «2026-08-26T11:18:31+02:00» → «26 de agosto de 2026». */
function enLargo(iso) {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} de ${MESES[m - 1]} de ${y}`;
}

/**
 * Los avisos de Actualidad de la ventana que publique el servidor, en su orden.
 * Nunca lanza: sin esto la portada se pinta igual, con una puerta menos.
 *
 * 🔴 **Se devuelven TODOS los que manden.** El carrusel recortaba a cuatro, y
 * eso dejaba fuera la mitad de lo publicado en el mes sin que se notara: hoy
 * llegan ocho y no se veían Sr. Chinarro, Blonde Redhead, The Strokes ni Echo &
 * The Bunnymen. Aquel cuatro venía de cuando la ventana la decidía la web; ahora
 * la decide el servidor, y cuántos hay es consecuencia suya, no nuestra.
 *
 * @param {number} [limite] tope opcional, para quien no quiera todos.
 */
export async function getAvisosActualidad(limite = 0) {
  const corta = (l) => (limite > 0 ? l.slice(0, limite) : l);

  const ahora = Date.now();
  if (cache && ahora - cacheAt <= TTL) return corta(cache);

  try {
    const r = await fetch(URL, { headers: { Accept: "application/json" } });
    if (!r.ok) return [];

    const d = await r.json();
    const avisos = Array.isArray(d?.avisos) ? d.avisos : [];

    const lista = avisos
      .map((a) => ({
        slug: a?.destino?.params?.slug || "",
        titulo: a?.titulo || "",
        // `meta` llega en castellano y con el número que toca —«Lanzamientos»—,
        // así que se usa tal cual en vez de traducir `tipo` por nuestra cuenta.
        categoria: a?.meta || "",
        fecha: enLargo(a?.fecha),
        imagen: a?.imagen || "",
      }))
      // Sin slug no hay a dónde ir, y sin imagen no hay tarjeta que pintar: la
      // puerta es un carrusel de carátulas. Un hueco negro es peor que una
      // tarjeta menos.
      .filter((a) => a.slug && a.imagen && a.titulo);

    // No se reordena: el servidor los manda ya ordenados y ese orden es suyo.
    cache = lista;
    cacheAt = ahora;
    return corta(lista);
  } catch (error) {
    return [];
  }
}
